import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper, Grid, TextField, RadioGroup, FormControlLabel, Radio, Typography, Stack, Button, Alert, Slide, MenuItem, Select, InputLabel, FormControl, Divider, CircularProgress } from '@mui/material';
import { saveManagerProbationReview, getAllProbationReviews, getProbationReview } from './ProbationReviewQueries';
import { getAllCorporateUsers } from '../CorporateQueries';
import DynamicFormRenderer from './DynamicFormRenderer';

const ratingOptions = ['Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];

const unwrap = (v) => (v && typeof v === 'object' && 'S' in v ? v.S : v);
const safeJSON = (v) => {
  const raw = unwrap(v);
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};
const getEmailFromLocalStorage = () => {
  const raw = localStorage.getItem('user');
  if (!raw) return '';
  try {
    const obj = JSON.parse(raw);
    return unwrap(obj.email || (obj.user && obj.user.email) || obj.user_email || obj.employee_email) || '';
  } catch {
    return raw.includes('@') ? raw : '';
  }
};

const normalizeUsers = (users = []) => users.map(u => ({
  email: unwrap(u.email || u.employee_email) || '',
  first_name: unwrap(u.first_name) || '',
  last_name: unwrap(u.last_name) || '',
  job_title: unwrap(u.job_title) || '',
  reporting_l1: unwrap(u.reporting_l1) || unwrap(u.manager) || '',
  reporting_l2: unwrap(u.reporting_l2) || '',
}));

const extractReview = (data) => {
  const item = (data && data.review) || (data && data.Item) || data || null;
  if (!item) return null;
  
  // Unwrap user_response
  const userRespRaw = unwrap(item.user_response);
  const user_response = safeJSON(userRespRaw) || userRespRaw || null;
  
  // Unwrap manager_response
  const mgrRespRaw = unwrap(item.manager_response);
  const manager_response = safeJSON(mgrRespRaw) || mgrRespRaw || null;
  
  // Unwrap second_level_manager_response
  const slmRespRaw = unwrap(item.second_level_manager_response);
  const second_level_manager_response = safeJSON(slmRespRaw) || slmRespRaw || null;
  
  const email = unwrap(item.email || item.employee_email) || '';
  const id = unwrap(item.id) || '';
  const periodStart = unwrap(item.period_start) || '';
  const periodEnd = unwrap(item.period_end) || '';
  const reviewPeriod = unwrap(item.review_period) || '';
  return { id, email, user_response, manager_response, second_level_manager_response, periodStart, periodEnd, reviewPeriod };
};

const listFromAll = (all) => {
  if (!all) return [];
  if (Array.isArray(all)) return all;
  if (Array.isArray(all.reviews)) return all.reviews;
  if (Array.isArray(all.Items)) return all.Items;
  if (all.reviews && Array.isArray(all.reviews.Items)) return all.reviews.Items;
  if (all.body && Array.isArray(all.body.reviews)) return all.body.reviews;
  if (all.body && Array.isArray(all.body.Items)) return all.body.Items;
  return [];
};

const hasManagerResponse = (resp) => {
  if (!resp || typeof resp !== 'object') return false;
  
  // Check for new schema field names (manager_test_case_design, manager_manual_testing, etc.)
  const newSchemaKeys = [
    'manager_test_case_design', 
    'manager_manual_testing', 
    'manager_product_knowledge', 
    'manager_dependability', 
    'manager_productivity', 
    'manager_teamwork', 
    'overall_performance', 
    'probation_progress'
  ];
  
  // Check for old schema field names (testCaseDesign, manualTesting, etc.)
  const oldSchemaKeys = [
    'testCaseDesign', 
    'manualTesting', 
    'productKnowledge', 
    'dependability', 
    'productivity', 
    'teamwork', 
    'overallPerformance', 
    'probationProgress'
  ];
  
  // Check both new and old schema field names
  const allKeys = [...newSchemaKeys, ...oldSchemaKeys];
  return allKeys.some(k => {
    const value = resp[k];
    return value && String(value).trim() !== '' && String(value).trim() !== 'undefined';
  });
};

const ManagerProbationForm = ({ disabled = false, selectedEmployeeEmail = '', selectedEmployeeId = '', reviewsCache = [], onSelectEmployee, details, refreshCache, hideEmployeeSelector = false, formSchema, passedReviewData }) => {
  const initialInputs = useMemo(() => ({
    testCaseDesign: '',
    manualTesting: '',
    productKnowledge: '',
    dependability: '',
    productivity: '',
    teamwork: '',
    overallPerformance: '',
    probationProgress: '',
  }), []);
  
  const [inputs, setInputs] = useState(initialInputs);
  const [reports, setReports] = useState([]);
  const [employeeEmail, setEmployeeEmail] = useState(selectedEmployeeEmail || '');
  const [employeeReviewId, setEmployeeReviewId] = useState('');
  const [employeeSelfReview, setEmployeeSelfReview] = useState(null);
  const [existingManagerReview, setExistingManagerReview] = useState(null);
  const [alert, setAlert] = useState({ open: false, severity: 'success', message: '' });
  const [loading, setLoading] = useState(false);
  const [allReviews, setAllReviews] = useState(Array.isArray(reviewsCache) ? reviewsCache : []);
  const [isPeriodActive, setIsPeriodActive] = useState(true);

  const blockedByNoSelfReview = !disabled && !employeeSelfReview;
  
  const isFormValid = useMemo(() => {
    // When using dynamic form renderer with schema
    if (formSchema) {
      const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
      const managerSections = schema.sections?.filter(s => s.completedBy === 'manager') || [];
      
      // Check all required fields are filled
      for (const section of managerSections) {
        for (const field of section.fields || []) {
          if (field.required && field.fieldType !== 'heading') {
            const value = inputs[field.fieldId];
            if (!value || String(value).trim() === '') {
              return false;
            }
          }
        }
      }
      return true;
    }
    
    // Legacy static form validation
    return (
      String(inputs.testCaseDesign || '').trim() !== '' &&
      String(inputs.manualTesting || '').trim() !== '' &&
      String(inputs.productKnowledge || '').trim() !== '' &&
      String(inputs.dependability || '').trim() !== '' &&
      String(inputs.productivity || '').trim() !== '' &&
      String(inputs.teamwork || '').trim() !== '' &&
      String(inputs.overallPerformance || '').trim() !== '' &&
      String(inputs.probationProgress || '').trim() !== ''
    );
  }, [inputs, formSchema]);

  // Check if period is active based on details
  useEffect(() => {
    if (!details.periodStart || !details.periodEnd) {
      setIsPeriodActive(true);
      return;
    }

    const now = new Date();
    const start = new Date(details.periodStart);
    const end = new Date(details.periodEnd);
    setIsPeriodActive(now >= start && now <= end);
  }, [details.periodStart, details.periodEnd]);

  // Sync selected props from parent
  useEffect(() => {
    if (selectedEmployeeEmail && selectedEmployeeEmail !== employeeEmail) {
      setEmployeeEmail(selectedEmployeeEmail);
      setInputs(initialInputs);
      setEmployeeSelfReview(null);
      setExistingManagerReview(null);
    }
    if (selectedEmployeeId && selectedEmployeeId !== employeeReviewId) {
      setEmployeeReviewId(selectedEmployeeId);
    }
  }, [selectedEmployeeEmail, selectedEmployeeId, employeeEmail, employeeReviewId, initialInputs]);

  // Sync reviews cache from parent
  useEffect(() => {
    if (Array.isArray(reviewsCache) && reviewsCache.length) {
      setAllReviews(reviewsCache);
    }
  }, [reviewsCache]);

  // Load reports list for managers
  useEffect(() => {
    const init = async () => {
      const me = getEmailFromLocalStorage();
      if (disabled) {
        setEmployeeEmail(me);
        setInputs(initialInputs);
        setEmployeeSelfReview(null);
        setExistingManagerReview(null);
        return;
      }
      try {
        const res = await getAllCorporateUsers();
        const users = normalizeUsers((res && res.users) || (res && res.Items) || res || []);
        const directReports = users.filter(u => (u.reporting_l1 || '').toLowerCase() === (me || '').toLowerCase());
        setReports(directReports);
        if (!selectedEmployeeEmail && directReports.length > 0) {
          setEmployeeEmail(directReports[0].email);
        }
      } catch (e) {
        setReports([]);
      }
    };
    init();
  }, [disabled, selectedEmployeeEmail, initialInputs]);

  // Load review data when selection changes
  useEffect(() => {
    const load = async () => {
      if (!employeeEmail || !details.periodStart) return;
      
      const managerEmail = getEmailFromLocalStorage();
      const draftKey = `managerProbationDraft_${managerEmail}_${employeeEmail}_${details.periodStart}`;
      
      try {
        let r = null;
        
        // Use passed review data if available (from table)
        if (passedReviewData) {
          r = extractReview(passedReviewData);
        } else {
          // Fetch all reviews to get the latest data
          const all = await getAllProbationReviews();
          const sourceList = listFromAll(all);
          setAllReviews(sourceList);
          
          // Find the specific review for this employee
          const mapped = sourceList.map(extractReview).filter(Boolean);
          r = mapped.find(x => 
            (x.email || '').toLowerCase() === employeeEmail.toLowerCase() && 
            x.periodStart === details.periodStart
          ) || null;
        }
        
        if (r) {
          const userResp = (r && r.user_response) || null;
          const mgrResp = (r && r.manager_response) || null;
          
          // Extract responses from structured format if needed
          let displayUserResp = userResp;
          let displayMgrResp = mgrResp;
          
          if (userResp && typeof userResp === 'object' && userResp.schema && userResp.responses) {
            displayUserResp = userResp.responses;
          }
          
          if (mgrResp && typeof mgrResp === 'object' && mgrResp.schema && mgrResp.responses) {
            displayMgrResp = mgrResp.responses;
          }
          
          setEmployeeSelfReview(displayUserResp);
          setEmployeeReviewId((r && r.id) || '');
          
          // Check if manager has actually submitted a response
          if (displayMgrResp && typeof displayMgrResp === 'object' && hasManagerResponse(displayMgrResp)) {
            // Manager has already submitted - show read-only view
            setExistingManagerReview(displayMgrResp);
            setInputs({}); // Clear inputs when showing existing
            localStorage.removeItem(draftKey);
          } else {
            // No manager review yet, load draft if exists
            setExistingManagerReview(null); // Clear existing when no manager review
            try {
              const draft = localStorage.getItem(draftKey);
              if (draft) {
                const parsed = JSON.parse(draft);
                setInputs(parsed);
              } else {
                setInputs(initialInputs);
              }
            } catch (e) {
              console.error('Failed to load draft:', e);
              setInputs(initialInputs);
            }
          }
        } else {
          // No review found in database
          setEmployeeSelfReview(null);
          setExistingManagerReview(null);
          setEmployeeReviewId('');
          setInputs(initialInputs);
        }
      } catch (e) {
        console.error('Failed to load review:', e);
        setEmployeeSelfReview(null);
        setExistingManagerReview(null);
        setEmployeeReviewId('');
        setInputs(initialInputs);
      }
    };
    load();
  }, [employeeEmail, details.periodStart, initialInputs, passedReviewData]);
  
  // Also listen to allReviews updates to refresh if needed
  useEffect(() => {
    if (!employeeEmail || !Array.isArray(allReviews) || allReviews.length === 0) return;
    
    const mapped = allReviews.map(extractReview).filter(Boolean);
    const r = mapped.find(x => 
      (x.email || '').toLowerCase() === employeeEmail.toLowerCase() && 
      x.periodStart === details.periodStart
    ) || null;
    
    if (r) {
      const userResp = (r && r.user_response) || null;
      const mgrResp = (r && r.manager_response) || null;
      
      // Extract responses from structured format if needed
      let displayUserResp = userResp;
      let displayMgrResp = mgrResp;
      
      if (userResp && typeof userResp === 'object' && userResp.schema && userResp.responses) {
        displayUserResp = userResp.responses;
      }
      
      if (mgrResp && typeof mgrResp === 'object' && mgrResp.schema && mgrResp.responses) {
        displayMgrResp = mgrResp.responses;
      }
      
      setEmployeeSelfReview(displayUserResp);
      
      // Only update manager review state if there's actual data
      if (displayMgrResp && typeof displayMgrResp === 'object' && hasManagerResponse(displayMgrResp)) {
        setExistingManagerReview(displayMgrResp);
        setInputs({}); // Clear inputs when showing existing
      }
    }
  }, [allReviews, employeeEmail, details.periodStart]);

  const handleChange = (e) => {
    if (disabled) return;
    setInputs(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Auto-save draft
  useEffect(() => {
    if (disabled || !employeeEmail || existingManagerReview || !isPeriodActive) return;
    
    const managerEmail = getEmailFromLocalStorage();
    const draftKey = `managerProbationDraft_${managerEmail}_${employeeEmail}_${details.periodStart}`;
    
    const timeoutId = setTimeout(() => {
      const hasContent = Object.values(inputs).some(v => String(v || '').trim() !== '');
      if (hasContent) {
        try {
          localStorage.setItem(draftKey, JSON.stringify(inputs));
          console.log('Manager probation review draft auto-saved for', employeeEmail);
        } catch (e) {
          console.error('Failed to save draft:', e);
        }
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [inputs, disabled, employeeEmail, existingManagerReview, details.periodStart, isPeriodActive]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (disabled || !employeeEmail || blockedByNoSelfReview) return;
    
    if (!isPeriodActive) {
      setAlert({ open: true, severity: 'error', message: 'Cannot submit review outside the active period.' });
      return;
    }

    setLoading(true);
    try {
      const managerEmail = getEmailFromLocalStorage();
      
      // Prepare response with schema structure
      let responseData = { ...inputs };
      
      // If using dynamic form with schema, include the schema with responses
      if (formSchema) {
        const schema = typeof formSchema === 'string' ? JSON.parse(formSchema) : formSchema;
        const managerSections = schema.sections?.filter(s => s.completedBy === 'manager') || [];
        
        // Create structured response with questions and answers
        const structuredResponse = {
          schema: managerSections,
          responses: inputs,
          submittedAt: new Date().toISOString(),
          submittedBy: managerEmail
        };
        
        responseData = structuredResponse;
      }
      
      await saveManagerProbationReview({ 
        responseData,
        ...details, 
        managerEmail, 
        employeeEmail, 
        employeeId: employeeReviewId 
      });
      
      const draftKey = `managerProbationDraft_${managerEmail}_${employeeEmail}_${details.periodStart}`;
      localStorage.removeItem(draftKey);
      
      setAlert({ open: true, severity: 'success', message: 'Manager probation review submitted successfully.' });
      
      // Refresh cache to reload the submitted data
      if (refreshCache) {
        await refreshCache();
      }
      
      // Set existing manager review to show read-only view immediately
      setExistingManagerReview({ ...inputs });
      setInputs({}); // Clear inputs when showing existing
    } catch (err) {
      setAlert({ open: true, severity: 'error', message: 'Failed to submit manager probation review.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      {/* Employee Selector */}
      {!disabled && !hideEmployeeSelector && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="employee-select-label">Select Employee</InputLabel>
          <Select 
            labelId="employee-select-label" 
            label="Select Employee" 
            value={employeeEmail} 
            onChange={(e) => { 
              setEmployeeEmail(e.target.value); 
              setEmployeeReviewId(''); 
              setInputs(initialInputs); 
              setEmployeeSelfReview(null); 
              setExistingManagerReview(null); 
              onSelectEmployee && onSelectEmployee(e.target.value); 
            }} 
            disabled={reports.length === 0}
          >
            {reports.map(r => (
              <MenuItem key={r.email} value={r.email}>
                {`${r.first_name || ''} ${r.last_name || ''}`.trim()} — {r.job_title || ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {!isPeriodActive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This review period is not currently active. Reviews can only be submitted during the active period.
        </Alert>
      )}

      {/* Employee Self Review Section */}
      {!disabled && (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Employee Self Review</Typography>
          <Divider sx={{ mb: 2 }} />
          {employeeSelfReview ? (
            formSchema ? (
              // Use DynamicFormRenderer to show employee responses with full questions
              <DynamicFormRenderer
                formSchema={formSchema}
                formData={employeeSelfReview}
                setFormData={() => {}}
                sectionFilter="employee"
                readOnly={true}
              />
            ) : (
              // Fallback to simple display
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Test Case Design:</strong> {employeeSelfReview.testCaseDesign || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Manual Testing:</strong> {employeeSelfReview.manualTesting || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Product Knowledge:</strong> {employeeSelfReview.productKnowledge || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Dependability:</strong> {employeeSelfReview.dependability || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Productivity:</strong> {employeeSelfReview.productivity || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Teamwork:</strong> {employeeSelfReview.teamwork || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Recommended Improvements:</strong> {employeeSelfReview.improvements || '-'}</Typography>
                </Grid>
              </Grid>
            )
          ) : (
            <Alert severity="warning">No self review submitted yet.</Alert>
          )}
        </Paper>
      )}

      {/* Manager Comments Section */}
      {disabled ? (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Comments</Typography>
          <Divider sx={{ mb: 2 }} />
          {hasManagerResponse(existingManagerReview) ? (
            formSchema ? (
              <DynamicFormRenderer
                formSchema={formSchema}
                formData={existingManagerReview}
                setFormData={() => {}}
                sectionFilter="manager"
                readOnly={true}
              />
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Test Case Design:</strong> {existingManagerReview.testCaseDesign || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Manual Testing:</strong> {existingManagerReview.manualTesting || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Product Knowledge:</strong> {existingManagerReview.productKnowledge || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Dependability:</strong> {existingManagerReview.dependability || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Productivity:</strong> {existingManagerReview.productivity || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2"><strong>Teamwork:</strong> {existingManagerReview.teamwork || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Overall Performance:</strong> {existingManagerReview.overallPerformance || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2"><strong>Probation Progress Assessment:</strong> {existingManagerReview.probationProgress || '-'}</Typography>
                </Grid>
              </Grid>
            )
          ) : (
            <Alert severity="info">Your first level manager has not submitted comments yet.</Alert>
          )}
        </Paper>
      ) : hasManagerResponse(existingManagerReview) ? (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Review</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the manager after reviewing the employee's self-evaluation</Typography>
          <Divider sx={{ mb: 2 }} />
          {formSchema ? (
            <DynamicFormRenderer
              formSchema={formSchema}
              formData={existingManagerReview}
              setFormData={() => {}}
              sectionFilter="manager"
              readOnly={true}
            />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Test Case Design:</strong> {existingManagerReview.testCaseDesign || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Manual Testing:</strong> {existingManagerReview.manualTesting || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Product Knowledge:</strong> {existingManagerReview.productKnowledge || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Dependability:</strong> {existingManagerReview.dependability || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Productivity:</strong> {existingManagerReview.productivity || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2"><strong>Teamwork:</strong> {existingManagerReview.teamwork || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Overall Performance:</strong> {existingManagerReview.overallPerformance || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2"><strong>Probation Progress Assessment:</strong> {existingManagerReview.probationProgress || '-'}</Typography>
              </Grid>
            </Grid>
          )}
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Manager Review</Typography>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>To be completed by the manager after reviewing the employee's self-evaluation</Typography>
          <Divider sx={{ mb: 2 }} />
          {blockedByNoSelfReview && (
            <Alert severity="info" sx={{ mb: 2 }}>
              The employee has not completed their self review yet. Manager review will be enabled after submission.
            </Alert>
          )}
          {alert.open && (
            <Slide in={alert.open} direction="down">
              <Alert severity={alert.severity} onClose={() => setAlert(a => ({ ...a, open: false }))}>{alert.message}</Alert>
            </Slide>
          )}
          {!blockedByNoSelfReview && (
            <Box component="form" onSubmit={handleSubmit}>
              {formSchema ? (
                // Use dynamic form renderer when schema is provided
                <DynamicFormRenderer
                  formSchema={formSchema}
                  formData={inputs}
                  setFormData={setInputs}
                  sectionFilter="manager"
                  readOnly={!isPeriodActive}
                />
              ) : (
              // Fallback to static form
              <>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary', mb: 3 }}>
                  To be completed by the manager after reviewing the employee's self-evaluation.
                </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. How effectively did the individual design clear, structured, and reusable test cases based on requirements, user stories, and acceptance criteria?
                </Typography>
                <RadioGroup name="testCaseDesign" value={inputs.testCaseDesign} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. How effectively did the individual perform manual testing and identify defects with clear reproduction steps and supporting evidence?
                </Typography>
                <RadioGroup name="manualTesting" value={inputs.manualTesting} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. How effectively did the individual improve their understanding of the product, testing tools, QA processes, and the mortgage domain during this period?
                </Typography>
                <RadioGroup name="productKnowledge" value={inputs.productKnowledge} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>4. How would you rate the individual on the following attributes?</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Dependability</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Reliable, steady, and trustworthy. Others can count on the individual to complete work on time and with good quality.
                </Typography>
                <RadioGroup name="dependability" value={inputs.dependability} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Productivity</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  The individual performs at the highest level of their abilities and uses their skills and resources efficiently to achieve the best possible outcome.
                </Typography>
                <RadioGroup name="productivity" value={inputs.productivity} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Teamwork</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  Collaborative and communicative. Others can count on the individual to work effectively with team members to complete tasks and maintain a positive work environment.
                </Typography>
                <RadioGroup name="teamwork" value={inputs.teamwork} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Overall Performance</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Taking into consideration the individual's experience level and role, how would you rate their overall performance during this review period?
                </Typography>
                <RadioGroup name="overallPerformance" value={inputs.overallPerformance} onChange={handleChange} row>
                  {ratingOptions.map(opt => (
                    <FormControlLabel 
                      key={opt} 
                      value={opt} 
                      control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                      label={opt} 
                    />
                  ))}
                </RadioGroup>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Probation Progress Assessment</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Based on the individual's performance during this review period, how would you assess their progress toward successfully completing the probation period?
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>Manager Recommendation:</Typography>
                <RadioGroup name="probationProgress" value={inputs.probationProgress} onChange={handleChange}>
                  <FormControlLabel 
                    value="On track to successfully complete probation" 
                    control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                    label="On track to successfully complete probation" 
                  />
                  <FormControlLabel 
                    value="Needs improvement during the remaining probation period" 
                    control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                    label="Needs improvement during the remaining probation period" 
                  />
                  <FormControlLabel 
                    value="Requires additional review" 
                    control={<Radio disabled={blockedByNoSelfReview || !isPeriodActive} />} 
                    label="Requires additional review" 
                  />
                </RadioGroup>
              </Grid>
            </Grid>
              </>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="flex-end" sx={{ mt: 2 }}>
              <Button variant="outlined" disabled={loading || !isPeriodActive}>Cancel</Button>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={loading || !employeeEmail || !isFormValid || !isPeriodActive} 
                startIcon={loading ? <CircularProgress size={16} /> : null}
              >
                Submit Manager Review
              </Button>
            </Stack>
          </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ManagerProbationForm;
